import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { UIService } from '@services';
import {
  AuthStore,
  ExtendedParametersTabsEnum,
  InstallationDetailsStore,
  RemoteDiagnosticsStore,
  RemoteDiagnosticsUseCasesStore,
  VddDetailsListState,
} from '@store';
import { concatLatestFrom } from '@ngrx/effects';
import { InformationPopupPage } from '../../../../shared/components/information-popup/information-popup.model';

@Component({
  selector: 'app-extended-parameters',
  templateUrl: './diagnostics.component.html',
  styleUrls: ['./diagnostics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsComponent implements OnInit, OnDestroy {
  readonly extendedParametersTabsEnum = ExtendedParametersTabsEnum;
  readonly InformationPopupPage = InformationPopupPage;

  selectedExtendedParametersTab$: Observable<ExtendedParametersTabsEnum>;
  gatewaySerial: string;
  installationId: number;
  isActiveSessionAvailable: boolean;
  vddDetailsList$: Observable<VddDetailsListState>;
  isParameterListEmpty: boolean;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    public activatedRoute: ActivatedRoute,
    public remoteDiagnosticsUseCasesStore: RemoteDiagnosticsUseCasesStore,
    public authStore: AuthStore,
    public uiService: UIService,
    private installationDetailsStore: InstallationDetailsStore,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];
    this.installationId = +this.activatedRoute.snapshot.params['id'];
    this.selectedExtendedParametersTab$ = this.remoteDiagnosticsUseCasesStore.getSelectedExtendedParametersTab(
      this.gatewaySerial
    );
    this.vddDetailsList$ = this.remoteDiagnosticsStore.vddDetailsList$;
    this.installationDetailsStore.loaded$
      .pipe(
        take(1),
        filter((areDetailsAlreadyLoaded) => !areDetailsAlreadyLoaded)
      )
      .subscribe(() => this.installationDetailsStore.readInstallationDetails(this.installationId));

    this.remoteDiagnosticsStore
      .isActiveSessionAvailable(this.gatewaySerial)
      .pipe(
        concatLatestFrom(() =>
          this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(this.gatewaySerial)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([isActiveSessionAvailable, mainLogicalLinkFromTopology]) => {
        this.isActiveSessionAvailable = isActiveSessionAvailable;
        this.cdr.detectChanges();

        if (isActiveSessionAvailable && mainLogicalLinkFromTopology) {
          this.remoteDiagnosticsUseCasesStore.loadUseCases(this.gatewaySerial);
        }
      });

    this.remoteDiagnosticsStore
      .isParameterListEmpty(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isParameterListEmpty) => {
        this.isParameterListEmpty = isParameterListEmpty;
        this.cdr.detectChanges();
        if (isParameterListEmpty) {
          this.remoteDiagnosticsUseCasesStore.changeUseCaseType(null, this.gatewaySerial);
          this.uiService.isAnyParameterRunning$.next(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
